import template from './sellops-order-modification-tracking-list.html.twig';
import './sellops-order-modification-tracking-list.scss';

const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Component.register('sellops-order-modification-tracking-list', {
    template,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('listing'),
        Mixin.getByName('notification'),
    ],

    data() {
        return {
            isLoading: false,
            items: null,
            sortBy: 'createdAt',
            sortDirection: 'DESC',
            total: 0,
            filterPaymentStatus: null,
            filterChangeType: null,
            filterModificationType: null,
            pendingCount: 0,
            refundCount: 0,
            completedCount: 0,
            isColumnManagerOpen: false,
            draggingColumnIndex: null,
            dragOverColumnIndex: null,
            columnOrder: null,
        };
    },

    metaInfo() {
        return {
            title: this.$createTitle(),
        };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('sellops_order_modification_tracking');
        },

        baseColumns() {
            return [
                {
                    property: 'parentOrderNumber',
                    label: this.$tc('sellops-order-modification-tracking.list.columnParentOrderNumber'),
                    allowResize: true,
                    width: '130px',
                    sortable: false,
                },
                {
                    property: 'childOrderNumber',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChildOrderNumber'),
                    allowResize: true,
                    width: '130px',
                    sortable: false,
                },
                {
                    property: 'modificationType',
                    label: this.$tc('sellops-order-modification-tracking.list.columnModificationType'),
                    allowResize: true,
                    width: '120px',
                },
                {
                    property: 'changeType',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChangeType'),
                    allowResize: true,
                    width: '110px',
                },
                {
                    property: 'differenceAmount',
                    label: this.$tc('sellops-order-modification-tracking.list.columnDifference'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'paymentStatus',
                    label: this.$tc('sellops-order-modification-tracking.list.columnPaymentStatus'),
                    allowResize: true,
                    width: '180px',
                },
                {
                    property: 'paymentMethodName',
                    label: this.$tc('sellops-order-modification-tracking.list.columnPaymentMethod'),
                    allowResize: true,
                    width: '140px',
                },
                {
                    property: 'orderStatus',
                    label: this.$tc('sellops-order-modification-tracking.list.columnOrderStatus'),
                    allowResize: true,
                    width: '110px',
                },
                {
                    property: 'parentOrderTotal',
                    label: this.$tc('sellops-order-modification-tracking.list.columnParentTotal'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'childOrderTotal',
                    label: this.$tc('sellops-order-modification-tracking.list.columnChildTotal'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                },
                {
                    property: 'refundAmount',
                    label: this.$tc('sellops-order-modification-tracking.list.columnRefundDue'),
                    allowResize: true,
                    align: 'right',
                    width: '120px',
                    sortable: false,
                },
                {
                    property: 'refundStatus',
                    label: this.$tc('sellops-order-modification-tracking.list.columnRefundStatus'),
                    allowResize: true,
                    width: '180px',
                    sortable: false,
                },
                {
                    property: 'createdAt',
                    label: this.$tc('sellops-order-modification-tracking.list.columnCreatedAt'),
                    allowResize: true,
                    width: '150px',
                },
            ];
        },

        orderedColumns() {
            const cols = [...this.baseColumns];
            if (!this.columnOrder || this.columnOrder.length === 0) {
                return cols;
            }
            const ordered = [];
            this.columnOrder.forEach((prop) => {
                const col = cols.find((c) => c.property === prop);
                if (col) ordered.push(col);
            });
            // Append any new columns not yet in the saved order
            cols.forEach((col) => {
                if (!this.columnOrder.includes(col.property)) ordered.push(col);
            });
            return ordered;
        },

        paymentStatusOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'pending_payment', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusPending') },
                { value: 'refund_required', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusRefund') },
                { value: 'completed', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusCompleted') },
            ];
        },

        paymentStatusSelectOptions() {
            return [
                { value: 'pending_payment', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusPending') },
                { value: 'refund_required', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusRefund') },
                { value: 'completed', label: this.$tc('sellops-order-modification-tracking.list.paymentStatusCompleted') },
            ];
        },

        refundStatusSelectOptions() {
            return [
                { value: 'refund_required', label: this.$tc('sellops-order-modification-tracking.list.refundStatusRequired') },
                { value: 'completed', label: this.$tc('sellops-order-modification-tracking.list.refundStatusCompleted') },
                { value: 'waived', label: this.$tc('sellops-order-modification-tracking.list.refundStatusWaived') },
            ];
        },

        changeTypeOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'increase', label: this.$tc('sellops-order-modification-tracking.list.changeTypeIncrease') },
                { value: 'decrease', label: this.$tc('sellops-order-modification-tracking.list.changeTypeDecrease') },
                { value: 'neutral', label: this.$tc('sellops-order-modification-tracking.list.changeTypeNeutral') },
            ];
        },

        modificationTypeOptions() {
            return [
                { value: '', label: this.$tc('sellops-order-modification-tracking.list.filterAll') },
                { value: 'supplement', label: this.$tc('sellops-order-modification-tracking.list.modificationTypeSupplement') },
                { value: 'replacement', label: this.$tc('sellops-order-modification-tracking.list.modificationTypeReplacement') },
            ];
        },

        listCriteria() {
            const criteria = new Criteria();

            criteria.setPage(this.page);
            criteria.setLimit(this.limit);
            criteria.setTotalCountMode(1);

            criteria.addSorting(Criteria.sort(this.sortBy, this.sortDirection));

            criteria.addAssociation('parentOrder');
            criteria.addAssociation('childOrder');

            if (this.filterPaymentStatus) {
                criteria.addFilter(Criteria.equals('paymentStatus', this.filterPaymentStatus));
            }

            if (this.filterChangeType) {
                criteria.addFilter(Criteria.equals('changeType', this.filterChangeType));
            }

            if (this.filterModificationType) {
                criteria.addFilter(Criteria.equals('modificationType', this.filterModificationType));
            }

            if (this.term) {
                criteria.setTerm(this.term);
            }

            return criteria;
        },
    },

    created() {
        try {
            const saved = localStorage.getItem('sellops_tracking_column_order');
            if (saved) {
                this.columnOrder = JSON.parse(saved);
            }
        } catch (e) {
            // ignore localStorage errors
        }
        this.getList();
    },

    methods: {
        async getList() {
            this.isLoading = true;

            try {
                const result = await this.repository.search(this.listCriteria, Shopware.Context.api);

                // Enrich items with flattened order numbers for easier grid rendering
                result.forEach((item) => {
                    item.parentOrderNumber = item.parentOrder ? item.parentOrder.orderNumber : (item.parentOrderId || '–');
                    item.childOrderNumber = item.childOrder ? item.childOrder.orderNumber : (item.childOrderId || '–');
                });

                this.items = result;
                this.total = result.total;

                await this.loadSummaryCounts();
            } catch (error) {
                this.createNotificationError({
                    message: error.message || 'Failed to load tracking data',
                });
            } finally {
                this.isLoading = false;
            }
        },

        async loadSummaryCounts() {
            try {
                const pendingCriteria = new Criteria();
                pendingCriteria.addFilter(Criteria.equals('paymentStatus', 'pending_payment'));
                pendingCriteria.setLimit(1);
                pendingCriteria.setTotalCountMode(1);
                const pendingResult = await this.repository.search(pendingCriteria, Shopware.Context.api);
                this.pendingCount = pendingResult.total;

                // Count replacement rows needing refund (refundStatus) plus
                // address-change rows needing refund (paymentStatus).
                const refundStatusCriteria = new Criteria();
                refundStatusCriteria.addFilter(Criteria.equals('refundStatus', 'refund_required'));
                refundStatusCriteria.setLimit(1);
                refundStatusCriteria.setTotalCountMode(1);
                const refundStatusResult = await this.repository.search(refundStatusCriteria, Shopware.Context.api);

                const paymentRefundCriteria = new Criteria();
                paymentRefundCriteria.addFilter(Criteria.equals('paymentStatus', 'refund_required'));
                paymentRefundCriteria.setLimit(1);
                paymentRefundCriteria.setTotalCountMode(1);
                const paymentRefundResult = await this.repository.search(paymentRefundCriteria, Shopware.Context.api);

                this.refundCount = (refundStatusResult.total || 0) + (paymentRefundResult.total || 0);

                const completedCriteria = new Criteria();
                completedCriteria.addFilter(Criteria.equals('paymentStatus', 'completed'));
                completedCriteria.setLimit(1);
                completedCriteria.setTotalCountMode(1);
                const completedResult = await this.repository.search(completedCriteria, Shopware.Context.api);
                this.completedCount = completedResult.total;
            } catch (error) {
                // Summary counts are non-critical
            }
        },

        onSortColumn(column) {
            if (column.dataIndex) {
                this.sortBy = column.dataIndex;
            } else {
                this.sortBy = column.property;
            }

            this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
            this.getList();
        },

        onChangeLanguage() {
            this.getList();
        },

        onFilterChange() {
            this.page = 1;
            this.getList();
        },

        capitalize(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1);
        },

        getVariantForChangeType(changeType) {
            const variants = {
                increase: 'warning',
                decrease: 'danger',
                neutral: 'info',
            };

            return variants[changeType] || 'neutral';
        },

        formatCurrency(amount, currencyIso) {
            return new Intl.NumberFormat(navigator.language, {
                style: 'currency',
                currency: currencyIso || 'EUR',
            }).format(amount || 0);
        },

        async onRefundStatusChange(item, newStatus) {
            item.refundStatus = newStatus;

            try {
                await this.repository.save(item, Shopware.Context.api);
                this.createNotificationSuccess({
                    message: this.$tc('sellops-order-modification-tracking.list.refundStatusUpdated'),
                });
                await this.loadSummaryCounts();
            } catch (error) {
                this.createNotificationError({
                    message: error.message,
                });
                this.getList();
            }
        },

        async onPaymentStatusChange(item, newStatus) {
            item.paymentStatus = newStatus;

            try {
                await this.repository.save(item, Shopware.Context.api);
                this.createNotificationSuccess({
                    message: this.$tc('sellops-order-modification-tracking.list.paymentStatusUpdated'),
                });
                await this.loadSummaryCounts();
            } catch (error) {
                this.createNotificationError({
                    message: error.message,
                });
                this.getList();
            }
        },

        onOpenParentOrder(item) {
            if (item.parentOrderId) {
                this.$router.push({ name: 'sw.order.detail', params: { id: item.parentOrderId } });
            }
        },

        onOpenChildOrder(item) {
            if (item.childOrderId) {
                this.$router.push({ name: 'sw.order.detail', params: { id: item.childOrderId } });
            }
        },

        goToSettings() {
            this.$router.push({ name: 'sellops.order.modification.tracking.config' });
        },

        toggleColumnManager() {
            this.isColumnManagerOpen = !this.isColumnManagerOpen;
        },

        getModificationTypeLabel(type) {
            const map = {
                supplement: this.$tc('sellops-order-modification-tracking.list.modificationTypeSupplement'),
                replacement: this.$tc('sellops-order-modification-tracking.list.modificationTypeReplacement'),
                shipping_refund: 'Shipping Refund',
                address_change: 'Address Change',
            };
            return map[type] || this.capitalize(type);
        },

        onColumnDragStart(index) {
            this.draggingColumnIndex = index;
        },

        onColumnDragOver(e, index) {
            e.preventDefault();
            this.dragOverColumnIndex = index;
        },

        onColumnDrop(index) {
            if (this.draggingColumnIndex === null || this.draggingColumnIndex === index) {
                this.draggingColumnIndex = null;
                this.dragOverColumnIndex = null;
                return;
            }
            const cols = [...this.orderedColumns];
            const dragged = cols.splice(this.draggingColumnIndex, 1)[0];
            cols.splice(index, 0, dragged);
            this.columnOrder = cols.map((c) => c.property);
            try {
                localStorage.setItem('sellops_tracking_column_order', JSON.stringify(this.columnOrder));
            } catch (e) {
                // ignore
            }
            this.draggingColumnIndex = null;
            this.dragOverColumnIndex = null;
        },

        onColumnDragEnd() {
            this.draggingColumnIndex = null;
            this.dragOverColumnIndex = null;
        },

        resetColumnOrder() {
            this.columnOrder = null;
            try {
                localStorage.removeItem('sellops_tracking_column_order');
            } catch (e) {
                // ignore
            }
        },
    },
});
